import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import { SubEventsService } from 'src/app/shared/services/data-api/sub-events.service';
import { ScoreEventsService } from 'src/app/shared/services/data-api/score-events.service';
import { QueryParams } from 'src/app/shared/models/query-params.interface';
import { TopEventsResponse } from 'src/app/shared/models/score-events.interface';
import {
  TopEventsParamsService,
  TopEventsTarget,
} from 'src/app/shared/utils/top-events-params.service';
import {
  ScannerInsightsData,
  SentimentEvent,
  ScoreLabEvent,
  MosaicScoreData,
} from './scanner-insights.models';
import {
  ScannerScoreStateService,
  ScannerScoreState,
} from '../scanner-score-state.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';

@Injectable({
  providedIn: 'root',
})
export class ScannerInsightsDataService {
  /** Expose score loading state from shared service */
  readonly scoreLoading$ = this.scannerScoreState.loading$;

  /** Expose score state from shared service */
  readonly scoreState$ = this.scannerScoreState.scoreState$;

  /** Expose score error from shared service */
  readonly scoreError$ = this.scannerScoreState.error$;

  constructor(
    private auth: AuthService,
    private subEvents: SubEventsService,
    private scoreEventsService: ScoreEventsService,
    private scannerScoreState: ScannerScoreStateService,
    private topEventsParams: TopEventsParamsService,
  ) {}

  fetchInsightsData(qparam: QueryParams): Observable<ScannerInsightsData> {
    const isSentiment = this.auth.scorelabSettings.defaultScore === 'sentiment';

    if (isSentiment) {
      return this.fetchSentimentInsights(qparam);
    } else {
      return this.fetchScoreLabInsights(qparam);
    }
  }

  getMosaicScoreFromState(
    state: ScannerScoreState | null,
    isSentiment: boolean,
  ): MosaicScoreData {
    if (!state) {
      return { value: undefined, displayValue: '' };
    }

    if (isSentiment) {
      const score = ScoreUtilsService.getLastScore(state.scoreSentiLongData);
      return this.formatSentimentScore(score);
    } else {
      const score = ScoreUtilsService.getLastScore(state.scoreData);
      return this.formatScoreLabScore(score);
    }
  }

  private fetchSentimentInsights(
    qparam: QueryParams,
  ): Observable<ScannerInsightsData> {
    return forkJoin({
      scoreState: this.scannerScoreState.fetchScoreData(qparam),
      subEvents: this.subEvents.fetchSubEventsList(qparam),
    }).pipe(
      map(({ scoreState, subEvents }) => ({
        mosaicScore: this.formatSentimentScore(
          ScoreUtilsService.getLastScore(scoreState.scoreSentiLongData),
        ),
        events: subEvents.events as SentimentEvent[],
        scoreEvents: null,
        isSentiment: true,
      })),
      catchError(() => of(this.getEmptyInsightsData(true))),
    );
  }

  private fetchScoreLabInsights(
    qparam: QueryParams,
  ): Observable<ScannerInsightsData> {
    return this.scannerScoreState.fetchScoreData(qparam).pipe(
      switchMap((scoreState) => {
        const mosaicScore = this.formatScoreLabScore(
          ScoreUtilsService.getLastScore(scoreState.scoreData),
        );
        return this.fetchTopEvents(mosaicScore).pipe(
          map((scoreEvents) => ({
            mosaicScore,
            events: null,
            scoreEvents,
            isSentiment: false,
          })),
        );
      }),
      catchError(() => of(this.getEmptyInsightsData(false))),
    );
  }

  private fetchTopEvents(
    mosaicScore: MosaicScoreData,
  ): Observable<ScoreLabEvent[]> {
    const scoreKey = ScoreUtilsService.resolveScoreKey(
      this.auth.scorelabSettings.defaultScore,
    );
    const target = this.topEventsParams.resolveTarget();

    const request$ = TopEventsParamsService.isBullBearScoreKey(scoreKey)
      ? this.dispatchBullBear(
          target,
          this.topEventsParams.buildBullBearRequest({}),
        )
      : this.dispatchScoreLab(
          target,
          this.topEventsParams.buildScoreLabRequest({
            scoreKey,
            topEventsSign: this.resolveCreditWatchSign(scoreKey, mosaicScore),
          }),
        );

    return request$.pipe(
      map((response) => response.top_events || []),
      catchError(() => of([])),
    );
  }

  private dispatchScoreLab(
    target: TopEventsTarget,
    params: Parameters<ScoreEventsService['getTopEvents']>[2],
  ): Observable<TopEventsResponse> {
    return target.kind === 'entity'
      ? this.scoreEventsService.getTopEvents(target.id, target.type, params)
      : this.scoreEventsService.getPortfolioTopEvents(target.content, params);
  }

  private dispatchBullBear(
    target: TopEventsTarget,
    params: Parameters<ScoreEventsService['getBullBearTopEvents']>[2],
  ): Observable<TopEventsResponse> {
    return target.kind === 'entity'
      ? this.scoreEventsService.getBullBearTopEvents(
          target.id,
          target.type,
          params,
        )
      : this.scoreEventsService.getPortfolioBullBearTopEvents(
          target.content,
          params,
        );
  }

  private resolveCreditWatchSign(
    scoreKey: string,
    mosaicScore: MosaicScoreData,
  ): 'neg' | undefined {
    return scoreKey === 'credit_watch' &&
      !!mosaicScore.value &&
      mosaicScore.value > 0
      ? 'neg'
      : undefined;
  }

  private formatSentimentScore(score: number | null): MosaicScoreData {
    if (score === null || score === undefined) {
      return { value: undefined, displayValue: '' };
    }
    const value = Math.round(score * 100);
    return { value, displayValue: `${value}%` };
  }

  private formatScoreLabScore(score: number | null): MosaicScoreData {
    if (score === null || score === undefined) {
      return { value: undefined, displayValue: '' };
    }
    const value = Number(score.toFixed(1));
    return { value, displayValue: `${value} / 10` };
  }

  private getEmptyInsightsData(isSentiment: boolean): ScannerInsightsData {
    return {
      mosaicScore: { value: undefined, displayValue: '' },
      events: isSentiment ? [] : null,
      scoreEvents: isSentiment ? null : [],
      isSentiment,
    };
  }

  calculateBarPercentage(events: SentimentEvent[], value: number): number {
    if (!events || events.length === 0) return 0;
    const counts = events.map((event) => event.count);
    const maxCount = Math.max(...counts);
    return maxCount > 0 ? (value * 100) / maxCount : 0;
  }

  calculateScoreBarPercentage(events: ScoreLabEvent[], value: number): number {
    if (!events || events.length === 0) return 0;
    const amounts = events.map((event) => event.contrib_amount);
    const maxAmount = Math.max(...amounts);
    return maxAmount > 0 ? (value * 100) / maxAmount : 0;
  }

  hasNonEmptyEvents(events: SentimentEvent[]): boolean {
    if (!events) return false;
    return events.some((item) => item.count > 0);
  }

  hasNonEmptyScoreEvents(events: ScoreLabEvent[]): boolean {
    if (!events) return false;
    return events.some((item) => item.contrib_amount > 0);
  }
}
